import { FormStore, useStoreState } from '@ariakit/react'
import { FC } from 'react'
import styled from 'styled-components'

import { Info, TypeStyle } from '@syconium/little-miss-figgy'

import { useTranslation } from '../../../../../../app/_providers/TranslationProvider.client'
import { useEmbroidery } from '../../../../../../containers/EmbroideryContainer/EmbroideryProvider'
import { DtcLogoSetupFormValues } from '../../../../../../containers/EmbroideryContainer/types'
import { RequiredAsterisk } from '../../../../../atoms/RequiredAsterisk'
import {
  ErrorMessage,
  Label,
  PriceLabel,
  StyledTooltip,
} from '../../../../Modal/primitives/shared-form-styles'
import { LogoFormContainer } from '../LogoFormContainer'

import { EmbroideryRadio } from './EmbroideryRadio'

interface Props {
  form: FormStore<DtcLogoSetupFormValues>
}

export const LogoColorSelector: FC<Props> = ({ form }) => {
  const { logoDisplayPrices } = LogoFormContainer.useContainer()
  const {
    embroidery: {
      modal: {
        forms: { dtcLogoEmbroideryForm: fixtures },
      },
    },
  } = useTranslation()
  const { embroiderySiteMessages } = useEmbroidery()
  const logoColorSelectionTooltip = embroiderySiteMessages?.logoColorSelectionTooltip
  const name = 'digitization_setting'

  const digitizationSetting = useStoreState(form, s => s.values.digitization_setting)
  const errorMessage = useStoreState(form, () => form.getError(name))

  return (
    <div role='group'>
      <RadioLabel>
        <span>
          <LogoColorLabel>{fixtures.logoColorLabel}</LogoColorLabel>
          <RequiredAsterisk />
        </span>
        {logoColorSelectionTooltip && (
          <StyledTooltip content={<>{logoColorSelectionTooltip}</>} placement='bottom-start'>
            <button type='button' aria-label={fixtures.ariaLabelLogoColorTooltip}>
              <Info height='12px' width='12px' fill='#282828' />
            </button>
          </StyledTooltip>
        )}
        {digitizationSetting && (
          <SelectedColorLabel>
            {digitizationSetting === 'BLACK_WHITE'
              ? fixtures.logoStrings.LOGO_BLACK_WHITE.title
              : fixtures.logoStrings.LOGO_COLOR.title}
          </SelectedColorLabel>
        )}
      </RadioLabel>
      <EmbroideryRadio.Label>
        <EmbroideryRadio.Button
          name={name}
          value='COLOR'
          type='radio'
          checked={digitizationSetting === 'COLOR'}
          onChange={() => form.setValue('digitization_setting', 'COLOR')}
          aria-label={fixtures.logoStrings.LOGO_COLOR.title}
        >
          {fixtures.logoStrings.LOGO_COLOR.title}
        </EmbroideryRadio.Button>
        <PriceLabel>{logoDisplayPrices.color}</PriceLabel>
      </EmbroideryRadio.Label>
      <EmbroideryRadio.Label>
        <EmbroideryRadio.Button
          name={name}
          value='BLACK_WHITE'
          type='radio'
          checked={digitizationSetting === 'BLACK_WHITE'}
          onChange={() => form.setValue('digitization_setting', 'BLACK_WHITE')}
          aria-label={fixtures.logoStrings.LOGO_BLACK_WHITE.title}
        >
          {fixtures.logoStrings.LOGO_BLACK_WHITE.title}
        </EmbroideryRadio.Button>
        <PriceLabel>{logoDisplayPrices.bw}</PriceLabel>
      </EmbroideryRadio.Label>
      {errorMessage && (
        <ErrorMessage inset role='alert'>
          {errorMessage}
        </ErrorMessage>
      )}
    </div>
  )
}

const RadioLabel = styled(Label)`
  display: flex;
  gap: ${o => o.theme.spacing(2)};
  margin-bottom: 0;
  align-items: center;
`

const LogoColorLabel = styled(TypeStyle.HeadlineMicro)`
  display: inline;
  letter-spacing: ${o => o.theme.typography.font.letterSpacing.wide};
  font-weight: ${o => o.theme.typography.font.weight[700]};
  line-height: ${o => o.theme.typography.font.lineHeight.loose};
`

const SelectedColorLabel = styled(TypeStyle.HeadlineMicro)`
  text-transform: capitalize;
  font-weight: ${o => o.theme.typography.font.weight[400]};
  color: ${o => o.theme.color.deprecated.obsidianLight};
  line-height: 18px;
`
